import { StickyTableHeader } from "vh-sticky-table-header";
import { onViewLoad } from "~/jose_modern/on_view_load";
import { debounce } from "lodash";
import { Spinner } from "~/jose_modern/spinner";
import { onceEach } from "~/jose_modern/once";

const COLUMN_HEADER_SELECTOR = "[role=columnheader]";
const NESTED_ROW_SELECTOR = "tr[data-depth]";
const SUBRECIPE_TOGGLE_SELECTOR = "[data-show-subrecipes]";

let stickyTable = null;
let cleanupSticky = null;

const setupStickyTable = (table, tableClone) => {
  cleanupSticky?.();
  // destroy() leaves its cloned header behind, and a new instance would add a second one.
  tableClone.replaceChildren();
  stickyTable = new StickyTableHeader(table, tableClone, { max: 0 });

  const handleTableClick = (event) => {
    if (
      event.target.matches(COLUMN_HEADER_SELECTOR) ||
      event.target.closest(COLUMN_HEADER_SELECTOR)
    ) {
      setTimeout(() => setupStickyTable(table, tableClone), 50);
    }
  };
  table.addEventListener("click", handleTableClick);

  cleanupSticky = () => {
    try {
      stickyTable?.destroy();
    } catch (error) {
      console.warn("Caught error while destroying sticky table", error);
    }
    table.removeEventListener("click", handleTableClick);
  };
};

const parentsByDepth = (rows) => {
  const parentOf = new Map();
  const ancestors = [];

  rows.forEach((row) => {
    const depth = depthOf(row);

    ancestors.length = depth;
    if (depth > 0) parentOf.set(row, ancestors[depth - 1]);
    ancestors[depth] = row;
  });

  return parentOf;
};

const groupedUnderParents = (sortedRows, parentOf) => {
  const childrenOf = new Map();
  const roots = [];

  sortedRows.forEach((row) => {
    const parent = parentOf.get(row);

    if (!parent) {
      roots.push(row);
      return;
    }

    if (!childrenOf.has(parent)) childrenOf.set(parent, []);
    childrenOf.get(parent).push(row);
  });

  const ordered = [];
  const visit = (row) => {
    ordered.push(row);
    (childrenOf.get(row) || []).forEach(visit);
  };
  roots.forEach(visit);

  return ordered;
};

const depthOf = (row) => Number(row.dataset.depth);

const setupSubrecipeGrouping = (table) => {
  const rows = Array.from(table.querySelectorAll(NESTED_ROW_SELECTOR));
  if (!rows.some((row) => depthOf(row) > 0)) return;

  const parentOf = parentsByDepth(rows);

  const handleAfterSort = () => {
    const sorted = Array.from(table.querySelectorAll(NESTED_ROW_SELECTOR));
    const body = sorted[0].parentNode;

    groupedUnderParents(sorted, parentOf).forEach((row) => body.appendChild(row));
  };

  table.addEventListener("afterSort", handleAfterSort);
};

const scanGrouping = (container) => {
  onceEach(
    container.querySelectorAll("#nutrition-breakdown-table"),
    "subrecipe-grouping",
    setupSubrecipeGrouping
  );
};

const setExpandParam = (element, show) => {
  if (element.tagName === "INPUT") {
    element.value = show ? "true" : "";
    return;
  }

  const url = new URL(element.href);
  if (show) {
    url.searchParams.set("expand_subrecipes", "true");
  } else {
    url.searchParams.delete("expand_subrecipes");
  }
  element.href = url.href;
};

const showSubrecipes = (table, show) => {
  table.querySelectorAll(NESTED_ROW_SELECTOR).forEach((row) => {
    if (depthOf(row) > 0) row.classList.toggle("is-hidden", !show);
  });

  table.querySelectorAll(SUBRECIPE_TOGGLE_SELECTOR).forEach((toggle) => {
    toggle.classList.toggle("is-hidden", toggle.dataset.showSubrecipes === String(show));
  });

  document.querySelectorAll("[data-expand-subrecipes-param]").forEach((element) => {
    setExpandParam(element, show);
  });

  // Column widths change with the rows shown, so the sticky header has to be measured again.
  scan();
};

const handleSubrecipeToggle = (event) => {
  const toggle = event.target.closest(SUBRECIPE_TOGGLE_SELECTOR);
  if (toggle) showSubrecipes(toggle.closest("table"), toggle.dataset.showSubrecipes === "true");
};

const scan = debounce(() => {
  cleanupSticky?.();

  const table = document.getElementById("nutrition-breakdown-table");
  const tableClone = document.getElementById("nutrition-breakdown-table-clone");

  if (table && tableClone) {
    setupStickyTable(table, tableClone);
  }

  setupSummarySheetDownload();
}, 200);

const setupSummarySheetDownload = () => {
  document.removeEventListener("stashed-file:ready", handleStashedFileReady);
  document.addEventListener("stashed-file:ready", handleStashedFileReady);
};

const handleStashedFileReady = () => {
  const activeDropdown = document.querySelector("#download-breakdown-summary.is-active");
  if (activeDropdown) activeDropdown.classList.remove("is-active");

  const downloadButton = document.querySelector("button#summary-sheet-download");
  if (downloadButton) Spinner.stop(downloadButton);
};

onViewLoad("recipes#breakdown", () => {
  scanGrouping(document);
  document
    .querySelector("[data-partial='recipes/breakdown_table']")
    ?.addEventListener("click", handleSubrecipeToggle);
  scan();
});
document.addEventListener("partial:replace", (ev) => {
  scanGrouping(ev.container);
  setTimeout(scan, 200);
});
window.addEventListener("beforeunload", () => cleanupSticky?.());
